/* eslint-disable max-lines */
'use client';

import React, { useState } from 'react';
import {
	Box, Input, Spinner, Text, InputProps, InputGroup, CloseButton,
	HStack,
	Stack,
} from '@chakra-ui/react';
import { LuSearch } from 'react-icons/lu';
import { useLocale } from 'next-intl';

type AutocompleteHookReturn = {
	isDebouncing: boolean;
	wrapperRef:   React.RefObject<HTMLElement>;
	value:        string;
	setValue:     (val: string) => void;
	suggestions:        {
		product: {
			name:        string;
			slug:        string;
			description: {
				en: string;
				th: string;
			};
			collection: {
				slug: string;
				name: string;
			};
		};
	}[];
	isLoading:          boolean;
	showSuggestions:    boolean;
	setShowSuggestions: (show: boolean) => void;
	handleSelect:       (name: string) => void;
	showMessage:        boolean;
};

type AutocompleteProps = {
	hook:               AutocompleteHookReturn;
	inputSize?:         InputProps['size'];
	onSelectSuggestion: (collectionId: string, value: string) => void;
	handleClear?:       () => void;
} & InputProps;

// eslint-disable-next-line complexity
const Autocomplete = ({
	hook,
	inputSize = 'md',
	onSelectSuggestion,
	handleClear,
	...inputProps
}: AutocompleteProps) => {
	const {
		isDebouncing,
		wrapperRef,
		value,
		setValue,
		suggestions,
		isLoading,
		showSuggestions,
		setShowSuggestions,
		showMessage,
	} = hook;
	const locale = useLocale() as 'en' | 'th';

	const [ isFocused, setIsFocused ] = useState(false);
	const isEmpty = value.trim().length === 0;
	const shouldShowDropdown = isFocused || showSuggestions;

	const showNoResults
		= !isDebouncing && !isLoading && !showMessage && !isEmpty && value.length >= 3 && suggestions.length === 0;

	const showSearching = isDebouncing && value.length >= 3;

	return (
		<Box ref={wrapperRef} maxW={'md'} position={'relative'}>
			<InputGroup
				endElement={
					value
						? (
							<CloseButton
								onClick={() => {
									setValue('');
									handleClear?.();
								}}
							/>
						)
						: undefined
				}
				startElement={<LuSearch />}
				width={'100%'}
			>
				<Input
					size={inputSize}
					value={value}
					onBlur={() => {
						setTimeout(() => {
							setIsFocused(false);
							setShowSuggestions(false);
						}, 150);
					}}
					onChange={e => setValue(e.target.value)}
					onFocus={() => {
						setIsFocused(true);
						setShowSuggestions(true);
					}}
					{...inputProps}
				/>
			</InputGroup>
			{shouldShowDropdown && (
				<Box
					bg={'white'}
					border={'1px solid'}
					borderColor={'gray.200'}
					maxH={'250px'}
					mt={'2'}
					overflowY={'auto'}
					position={'absolute'}
					rounded={'md'}
					shadow={'md'}
					top={'100%'}
					width={'100%'}
					zIndex={1100}
				>
					{isLoading && (
						<Box
							alignItems={'center'}
							display={'flex'}
							px={'4'}
							py={'3'}
						>
							<Spinner mr={'2'} size={'sm'} />
							<Text color={'gray.600'} fontSize={'sm'}>
								Loading...
							</Text>
						</Box>
					)}
					{showSearching && (
						<Box px={'4'} py={'3'}>
							<Text color={'gray.500'} fontSize={'sm'}>
								Searching...
							</Text>
						</Box>
					)}
					{!isLoading && isEmpty && (
						<Box px={'4'} py={'3'}>
							<Text color={'gray.500'} fontSize={'sm'}>
								Start typing to search our product.
							</Text>
						</Box>
					)}
					{!isLoading && !isEmpty && showMessage && (
						<Box px={'4'} py={'3'}>
							<Text color={'gray.500'} fontSize={'sm'}>
								Please enter at least 3 characters to get better search results.
							</Text>
						</Box>
					)}
					{!isLoading && suggestions.length > 0 && (
						<Stack gap={0} py={2}>
							{suggestions.map(suggestion => (
								<Box
									key={suggestion.product.slug}
									_hover={{ bg: 'gray.100' }}
									cursor={'pointer'}
									px={4}
									py={2}
									onClick={() => {
										onSelectSuggestion(suggestion.product.collection.slug, suggestion.product.slug);
										setValue(suggestion.product.slug);
									}}
								>
									<Stack gap={1}>
										<HStack justifyContent={'space-between'}>
											<Text fontSize={'16px'} fontWeight={'bold'}>{suggestion.product.name}</Text>
											<Text fontSize={'14px'} fontWeight={'normal'}>{suggestion.product.collection.name}</Text>
										</HStack>
										<Text
											color={'gray.500'}
											fontSize={'12px'}
											fontWeight={'normal'}
											lineClamp={1}
											maxLines={1}
										>{suggestion.product.description[locale]}
										</Text>
									</Stack>
								</Box>
							))}
						</Stack>
					)}
					{showNoResults && (
						<Box px={'4'} py={'3'}>
							<Text color={'gray.500'} fontSize={'sm'}>
								No results found.
							</Text>
						</Box>
					)}
				</Box>
			)}
		</Box>
	);
};

export default Autocomplete;
